import {PersonCloseCallCoverage} from '../../types'

/**
 * How much of the close-call question the data can actually answer.
 *
 * An empty close-call list has several possible meanings and they are not
 * equivalent — "recognition ran and nothing came close" is a finding, while
 * "recognition never had an opinion here" is missing data. Presenting the
 * second as the first hands an operator a confidently wrong answer to relay to
 * a parent, which is the whole reason coverage exists.
 *
 * Derived in one place, exported, and unit-tested directly rather than through
 * the DOM: this is the delta's entire decision logic, and routing every state
 * through a full view render is what left states untested last time.
 */
export type CoverageState =
  /** The server did not send coverage — an older faces during a rolling deploy. */
  | {kind: 'unknown'}
  /** No manual tags at all this season, so there is nothing to compare. */
  | {kind: 'noManualTags'}
  /** None of the photos were scanned by recognition. */
  | {kind: 'noTelemetry'; total: number}
  /**
   * The photos WERE scanned, but recognition never considered this camper on
   * any of them — it had no enrolled face to match at the time. Distinct from
   * noTelemetry because the remedy is different: check the training date.
   */
  | {kind: 'notACandidate'; scanned: number; total: number}
  /** Some photos are answerable, some are not, so the list may be incomplete. */
  | {kind: 'partial'; answerable: number; total: number}
  /** Every manually tagged photo is answerable — an empty list is a real finding. */
  | {kind: 'full'}

/**
 * `null` coverage means UNKNOWN, and must not be defaulted to zeros.
 *
 * Zeros are not an absence of claim — `manualTaggedPhotos === 0` asserts "this
 * camper has no manual tags this season", which is a STRONGER statement than
 * saying nothing, and false whenever the field is merely absent. It can also
 * contradict the stats panel on the same screen (which may show 7 manual tags).
 */
export const resolveCoverageState = (
  coverage: PersonCloseCallCoverage | null,
): CoverageState => {
  if (coverage === null) return {kind: 'unknown'}

  // Coerced because these arrive from JSON: a string count would make '4' >=
  // '10' evaluate true and silently suppress the incompleteness warning.
  const total = Number(coverage.manualTaggedPhotos)
  const scanned = Number(coverage.manualTaggedWithTelemetry)
  const answerable = Number(coverage.manualTaggedWithCandidateTelemetry)

  if (!Number.isFinite(total) || !Number.isFinite(answerable)) {
    return {kind: 'unknown'}
  }

  if (total === 0) return {kind: 'noManualTags'}

  if (answerable === 0) {
    // The photos were scanned for somebody, just never for this camper.
    if (Number.isFinite(scanned) && scanned > 0) {
      return {kind: 'notACandidate', scanned, total}
    }
    return {kind: 'noTelemetry', total}
  }

  if (answerable < total) return {kind: 'partial', answerable, total}

  return {kind: 'full'}
}

/** Whether an empty list may be presented as a genuine finding. */
export const isConclusive = (state: CoverageState): boolean =>
  state.kind === 'full'
